import * as R from 'ramda';
import LangEnum from '../../../../../lang/LangEnum';
import {
  GroupByColorClassificationTypeInfo,
  OrderHeadersGroupBy,
  OrderHeadersGroupByEnum,
  OrderProduct,
  Section,
} from '../../orderProducts.types';
import OrderProductsBuilder, { OrderProductsGroupProps } from './OrderProductsGroupBuilder';

const EMPTY_TITLE_ENTITY_BY_GROUP_BY: Partial<Record<OrderHeadersGroupByEnum, LangEnum>> = {
  [OrderHeadersGroupByEnum.CATEGORY]: LangEnum.GLOBAL_CATEGORY,
  [OrderHeadersGroupByEnum.SUB_CATEGORY]: LangEnum.GLOBAL_SUB_CATEGORY,
  [OrderHeadersGroupByEnum.COLLECTION]: LangEnum.GLOBAL_COLLECTION,
};

const getEmptyTitleEntity = (title?: string, groupByType?: OrderHeadersGroupBy) =>
  title || !groupByType ? undefined : EMPTY_TITLE_ENTITY_BY_GROUP_BY[groupByType];

class OrderProductsGroupByMultiple extends OrderProductsBuilder {
  private groupByTypes: OrderHeadersGroupBy[];

  constructor(groupByTypes: OrderHeadersGroupBy[]) {
    super();
    this.groupByTypes = groupByTypes;
  }

  public group({ orderProducts, info }: OrderProductsGroupProps): Section[] {
    const classificationTypes = info?.classificationTypeId || [];

    const groupedProducts = R.groupBy<OrderProduct>(
      (product) =>
        this.getGroupingKey(product, this.groupByTypes[0], classificationTypes?.[0]) || '_',
      orderProducts,
    );

    const formattedGroupedProducts = R.map((productsGroup: OrderProduct[]) =>
      R.groupBy<OrderProduct>(
        (product) =>
          this.getGroupingKey(
            product,
            this.groupByTypes[1],
            classificationTypes?.[1] ?? classificationTypes?.[0],
          ) || '_',
        productsGroup,
      ),
    )(groupedProducts as unknown as readonly OrderProduct[][]);

    const convertedSections = this.convertToSections({
      groupedProducts: formattedGroupedProducts as Record<string, OrderProduct[]>[],
      keyTrail: [],
      products: orderProducts,
      groupByIndex: 0,
      info,
    });
    return convertedSections;
  }

  private convertToSections({
    groupedProducts,
    keyTrail,
    products,
    groupByIndex,
    info,
  }: {
    groupedProducts: Record<string, OrderProduct[]>[];
    keyTrail: string[];
    products: OrderProduct[];
    groupByIndex: number;
    info?: GroupByColorClassificationTypeInfo;
  }): Section[] {
    const groupedProductsToPairs = R.toPairs(groupedProducts) as unknown as [
      string,
      OrderProduct[] | Record<string, OrderProduct[]>[],
    ][];

    return groupedProductsToPairs.flatMap(
      ([key, productsGroup]: [string, OrderProduct[] | Record<string, OrderProduct[]>[]]) => {
        const currentProduct = (
          Array.isArray(productsGroup) ? productsGroup[0] : products[0]
        ) as OrderProduct;

        const hasWindowGroup = this.groupByTypes.includes(OrderHeadersGroupByEnum.DELIVERY_WINDOW);

        const newKeyTrail = [...keyTrail, key].filter((part) => part);

        const availability = hasWindowGroup ? currentProduct.availability || '' : '';
        const deliveryWindow = hasWindowGroup ? currentProduct.deliveryWindow || null : null;

        if (Array.isArray(productsGroup)) {
          const sectionIdentifier = newKeyTrail.join('_').replaceAll('__', '_');

          const allFilterIsColorClassification = this.groupByTypes?.every(
            (type) => type === OrderHeadersGroupByEnum.COLOR_CLASSIFICATION_TYPE,
          );

          const classifications = info?.classificationTypeId;

          const [parentTitle, ...subGroupTitles] = sectionIdentifier
            .split('_')
            .map((keyName, index) => {
              const classificationType = allFilterIsColorClassification
                ? classifications?.[index]
                : keyName;

              return this.getCurrentProductTitle(
                currentProduct,
                this.groupByTypes?.[index],
                classificationType,
              );
            });

          const groupTitle = subGroupTitles.filter((part) => part).join(' - ');

          return [
            {
              groupTitle,
              groupTitleFallbackEntity: getEmptyTitleEntity(groupTitle, this.groupByTypes?.[1]),
              parentGroupKey: newKeyTrail[0],
              parentGroupTitle: parentTitle,
              parentGroupTitleFallbackEntity: getEmptyTitleEntity(
                parentTitle,
                this.groupByTypes?.[0],
              ),
              availability,
              deliveryWindow,
              productsCount: productsGroup.length,
              isExpanded: true,
              products: productsGroup as OrderProduct[],
              identifier: sectionIdentifier,
            } as Section,
          ];
        }

        return this.convertToSections({
          groupedProducts: productsGroup as Record<string, OrderProduct[]>[],
          keyTrail: newKeyTrail,
          products: productsGroup as OrderProduct[],
          groupByIndex: groupByIndex + 1,
          info,
        });
      },
    ) as Section[];
  }

  private getGroupingKey = (
    product: OrderProduct,
    groupByType: OrderHeadersGroupByEnum,
    classificationType?: string,
  ) => {
    switch (groupByType) {
      case OrderHeadersGroupByEnum.DELIVERY_WINDOW:
        return product.deliveryWindow?.id;
      case OrderHeadersGroupByEnum.CATEGORY:
        return product.category?.parent?.id || product.category?.id;
      case OrderHeadersGroupByEnum.SUB_CATEGORY:
        return product.category?.id;
      case OrderHeadersGroupByEnum.PRODUCT:
        return product.code;
      case OrderHeadersGroupByEnum.COLLECTION:
        return product.collection?.id;
      case OrderHeadersGroupByEnum.COLOR_CLASSIFICATION_TYPE:
        const foundClassification = product.colorClassifications?.find(
          (item) => classificationType === item.classificationType?.id,
        );
        return foundClassification?.id || '';
    }
  };

  private getCurrentProductTitle = (
    currentProduct: OrderProduct,
    type: OrderHeadersGroupByEnum,
    classificationType?: string,
  ) => {
    switch (type) {
      case OrderHeadersGroupByEnum.DELIVERY_WINDOW:
        return '';
      case OrderHeadersGroupByEnum.CATEGORY:
        return currentProduct.category?.parent?.name || currentProduct.category?.name;
      case OrderHeadersGroupByEnum.SUB_CATEGORY:
        return currentProduct.category?.name;
      case OrderHeadersGroupByEnum.PRODUCT:
        return `${currentProduct.code} - ${currentProduct.name}`;
      case OrderHeadersGroupByEnum.COLLECTION:
        return currentProduct.collection?.name;
      case OrderHeadersGroupByEnum.COLOR_CLASSIFICATION_TYPE:
        const foundClassification = currentProduct.colorClassifications?.find(
          (item) =>
            classificationType === item.classificationType?.id || classificationType === item.id,
        );
        return foundClassification?.name || '-';
    }
  };
}

export default OrderProductsGroupByMultiple;
